import { featureKeys as assetFeatureKeys } from '@/api/equipment/assets';
import { useCreateInspectionLog } from '@/api/equipment/inspection-logs/createInspectionLog';
import { useDeleteInspectionLog } from '@/api/equipment/inspection-logs/deleteInspectionLog';
import { useUpdateInspectionLog } from '@/api/equipment/inspection-logs/updateInspectionLog';
import { featureKeys as inspectionFeatureKeys } from '@/api/equipment/inspections';
import { featureKeys as vehicleFeatureKeys } from '@/api/equipment/vehicles';
import { FormDate, FormInput, FormTextarea } from '@/components/Form';
import { FormFileInput } from '@/components/Form/FormFileInput';
import { Button } from '@/components/ui/elements/button';
import { Form } from '@/components/ui/form/form';
import queryClient from '@/lib/react-query';
import { Inspection, InspectionLog } from '@/types';
import { formatDate } from '@/utils/format';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface InspectionLogFormProps {
  inspection: Inspection;
  inspectionLog: InspectionLog | undefined;
}

const schema = z.object({
  name: z.string(),
  performed_at: z.coerce.string(),
  usage_reading: z.coerce.number().nullish(),
  notes: z.string().nullish(),
  upload: z
    .custom<File>((v) => v instanceof File, {
      message: 'File is required',
    })
    .nullish(),
});

export const InspectionLogForm = ({ inspection, inspectionLog }: InspectionLogFormProps) => {
  const navigate = useNavigate();

  const { mutate: createInspectionLog } = useCreateInspectionLog();
  const { mutate: updateInspectionLog } = useUpdateInspectionLog();
  const { mutate: deleteLog } = useDeleteInspectionLog();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: inspection?.name ?? '',
      performed_at: inspectionLog?.performed_at ?? formatDate(new Date(), 'yyyy-MM-dd'),
      usage_reading: inspectionLog?.usage_reading ?? null,
      notes: inspectionLog?.notes ?? '',
      upload: null,
    },
  });

  const handleSubmit = (values: z.infer<typeof schema>) => {
    if (inspectionLog) {
      handleUpdate(values);
    } else {
      handleCreate(values);
    }
  };

  const handleCreate = (values: z.infer<typeof schema>) => {
    if (!inspection) return;

    createInspectionLog(
      {
        inspection,
        data: {
          ...values,
          due_at: inspection.is_past_due ? inspection.last_expected_date : inspection.next_due_date,
        },
      },
      {
        onSuccess: (inspectionLog) => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: inspectionFeatureKeys.all });
          navigate({
            to: `/fs/equipment/inspections/${inspection.id}/inspection-logs/${inspectionLog.id}`,
          });
        },
      },
    );
  };

  const handleUpdate = (values: z.infer<typeof schema>) => {
    if (!inspection || !inspectionLog) return;

    updateInspectionLog(
      {
        inspection,
        id: inspectionLog.id,
        data: values,
      },
      {
        onSuccess: (inspectionLog) => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
        },
      },
    );
  };

  const handleDelete = () => {
    if (!inspection || !inspectionLog) return;

    deleteLog(
      {
        inspection,
        id: inspectionLog.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: inspectionFeatureKeys.all });
          navigate({
            to: `/equipment/dashboard`,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (inspection) {
      form.reset({
        name: inspection?.name ?? '',
      });
    }
  }, [inspection]);

  useEffect(() => {
    if (inspectionLog) {
      form.reset({
        name: inspectionLog.inspection.name,
        performed_at: inspectionLog.performed_at ?? '',
        usage_reading: inspectionLog.usage_reading,
        notes: inspectionLog.notes ?? '',
      });
    }
  }, [inspectionLog]);

  if (!inspection) return <>Loading...</>;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col space-y-3 m-6">
        <FormInput control={form.control} name="name" label="Name" />
        <FormDate control={form.control} name="performed_at" label="Performed At" />
        {inspection.usage_unit && (
          <FormInput
            control={form.control}
            name="usage_reading"
            label={inspection.usage_unit}
            type="number"
          />
        )}
        <FormTextarea control={form.control} name="notes" label="Notes" />
        <FormFileInput control={form.control} name="upload" />
        <Button type="submit">{inspectionLog ? 'Update' : 'Next'}</Button>
      </form>
    </Form>
  );
};
